import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { Button } from "./ui/button";
import { HardDrives, FileDoc, FilePdf, FileCsv, ShieldCheck, ArrowClockwise, GitDiff } from "@phosphor-icons/react";

export default function PlatformBaseline() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [cmpFrom, setCmpFrom] = useState("");
  const [cmpTo, setCmpTo] = useState("");
  const [cmpBusy, setCmpBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/platform-baseline");
      setRows(data.snapshots || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportDoc = async (r, fmt) => {
    const key = `${r.id}-${fmt}`;
    setBusyId(key);
    const ext = fmt === "docx" ? "docx" : fmt === "csv" ? "csv" : "pdf";
    const base = fmt === "csv" ? "ClaroShield_Platform_Software_FULL" : "ClaroShield_Platform_Baseline";
    const fname = `${base}_${(r.build_id || "").slice(0, 12)}_${r.id.slice(0, 8)}.${ext}`;
    try { await downloadBlob(`/admin/platform-baseline/${r.id}/export?fmt=${fmt}`, fname); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generation failed"); }
    finally { setBusyId(""); }
  };

  const runCompare = async (fmt) => {
    if (!cmpFrom || !cmpTo) { toast.error("Pick two baselines to compare."); return; }
    if (cmpFrom === cmpTo) { toast.error("Pick two different baselines."); return; }
    setCmpBusy(true);
    const ext = fmt === "docx" ? "docx" : "pdf";
    const fname = `ClaroShield_Platform_Comparison_${cmpFrom.slice(0, 6)}_${cmpTo.slice(0, 6)}.${ext}`;
    try { await downloadBlob(`/admin/platform-baseline/compare?from_id=${cmpFrom}&to_id=${cmpTo}&fmt=${fmt}`, fname); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Comparison failed"); }
    finally { setCmpBusy(false); }
  };

  const optLabel = (r) => `${(r.build_id || "").slice(0, 10)} · ${(r.captured_at || "").slice(0, 16).replace("T", " ")}`;

  return (
    <div className="p-6 space-y-4 border border-border bg-card" data-testid="platform-baseline-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <HardDrives size={22} weight="duotone" />
            <h3 className="font-display font-bold text-lg">Platform Baseline Documents</h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Automated configuration baseline of the ClaroShield platform itself — OS/firmware, commercial &amp; open-source
            software, custom software, logical ports, and security patches (from the CVE history) — with a color-coded change
            <b> Diff</b> and justification. Maintained for internal ClaroCompliance change-awareness and to share our processes
            with customers on request. A PDF is generated and emailed to Claro Super Admins automatically on every
            configuration change (production). Generate any snapshot on demand below in Word or PDF.
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-1 shrink-0" onClick={load} data-testid="platform-baseline-refresh">
          <ArrowClockwise size={14} /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2 border border-border bg-secondary/20 px-3 py-3" data-testid="platform-baseline-compare-bar">
        <div className="flex items-center gap-1 text-sm font-semibold text-foreground mr-1">
          <GitDiff size={16} /> Compare builds
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-muted-foreground mb-0.5">From (older)</label>
          <select value={cmpFrom} onChange={(e) => setCmpFrom(e.target.value)} data-testid="platform-baseline-compare-from"
            className="text-xs border border-border bg-background px-2 py-1 min-w-[220px]">
            <option value="">Select baseline…</option>
            {rows.map((r) => <option key={r.id} value={r.id}>{optLabel(r)}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-muted-foreground mb-0.5">To (newer)</label>
          <select value={cmpTo} onChange={(e) => setCmpTo(e.target.value)} data-testid="platform-baseline-compare-to"
            className="text-xs border border-border bg-background px-2 py-1 min-w-[220px]">
            <option value="">Select baseline…</option>
            {rows.map((r) => <option key={r.id} value={r.id}>{optLabel(r)}</option>)}
          </select>
        </div>
        <button onClick={() => runCompare("pdf")} disabled={cmpBusy} data-testid="platform-baseline-compare-pdf"
          className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs text-foreground hover:border-red-600 hover:text-red-700 disabled:opacity-40"><FilePdf size={14} /> {cmpBusy ? "…" : "Compare (PDF)"}</button>
        <button onClick={() => runCompare("docx")} disabled={cmpBusy} data-testid="platform-baseline-compare-docx"
          className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs text-foreground hover:border-blue-600 hover:text-blue-700 disabled:opacity-40"><FileDoc size={14} /> {cmpBusy ? "…" : "Compare (Word)"}</button>
      </div>

      <div className="border border-border">
        <table className="w-full text-sm" data-testid="platform-baseline-table">
          <thead className="bg-secondary/40 overline text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Build</th>
              <th className="px-3 py-2 text-left">Captured</th>
              <th className="px-3 py-2 text-left">Trigger</th>
              <th className="px-3 py-2 text-left">OS / firmware</th>
              <th className="px-3 py-2 text-right">SW</th>
              <th className="px-3 py-2 text-right">Ports</th>
              <th className="px-3 py-2 text-right">Svc</th>
              <th className="px-3 py-2 text-right">Δ vs prev</th>
              <th className="px-3 py-2 text-right">Document</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground" data-testid="platform-baseline-loading">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground" data-testid="platform-baseline-empty">
                No baselines captured yet — one is captured automatically on the next scan/boot.
              </td></tr>
            ) : rows.map((r) => {
              const dc = r.delta_counts || {};
              const sd = r.services_delta || {};
              const drift = (dc.added || 0) + (dc.removed || 0) + (dc.changed || 0) + (dc.ports_added || 0) + (dc.ports_removed || 0) + (sd.added || 0) + (sd.removed || 0) + (r.os_changed ? 1 : 0);
              const parts = [];
              if ((dc.added || 0) + (dc.removed || 0) + (dc.changed || 0)) parts.push(`sw +${dc.added || 0} −${dc.removed || 0} ~${dc.changed || 0}`);
              if ((dc.ports_added || 0) + (dc.ports_removed || 0)) parts.push(`ports +${dc.ports_added || 0} −${dc.ports_removed || 0}`);
              if ((sd.added || 0) + (sd.removed || 0)) parts.push(`svc +${sd.added || 0} −${sd.removed || 0}`);
              if (r.os_changed) parts.push("OS changed");
              return (
                <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`platform-baseline-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{(r.build_id || "").slice(0, 12)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">{(r.captured_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-xs uppercase tracking-wide">{r.trigger}</td>
                  <td className="px-3 py-2 text-xs">{r.os}</td>
                  <td className="px-3 py-2 text-right">{r.software_count}</td>
                  <td className="px-3 py-2 text-right">{r.ports_count}</td>
                  <td className="px-3 py-2 text-right">{r.services_count}</td>
                  <td className="px-3 py-2 text-right">
                    {drift === 0
                      ? <span className="inline-flex items-center gap-1 text-green-700"><ShieldCheck size={13} weight="fill" /> None</span>
                      : <span className="text-amber-700 font-semibold text-xs">{parts.join(" · ")}</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => exportDoc(r, "pdf")} disabled={busyId === `${r.id}-pdf`} title="Generate baseline document (PDF)" data-testid={`platform-baseline-pdf-${r.id}`} className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs text-foreground hover:border-red-600 hover:text-red-700 disabled:opacity-40"><FilePdf size={14} /> {busyId === `${r.id}-pdf` ? "…" : "PDF"}</button>
                      <button onClick={() => exportDoc(r, "docx")} disabled={busyId === `${r.id}-docx`} title="Generate baseline document (Word)" data-testid={`platform-baseline-docx-${r.id}`} className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs text-foreground hover:border-blue-600 hover:text-blue-700 disabled:opacity-40"><FileDoc size={14} /> {busyId === `${r.id}-docx` ? "…" : "Word"}</button>
                      <button onClick={() => exportDoc(r, "csv")} disabled={busyId === `${r.id}-csv`} title="Download the complete, uncapped software inventory (CSV)" data-testid={`platform-baseline-csv-${r.id}`} className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs text-foreground hover:border-green-600 hover:text-green-700 disabled:opacity-40"><FileCsv size={14} /> {busyId === `${r.id}-csv` ? "…" : "Full SW (CSV)"}</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
